import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image, InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const OwnerProfilesManager = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    profile_image: '',
    bio: '',
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    experience: '',
    professional_roles: '',
    qualifications: '',
    status: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchProfiles();
  }, []);

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      email: '',
      profile_image: '',
      bio: '',
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      experience: '',
      professional_roles: '',
      qualifications: '',
      status: true
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/owner_profiles.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedProfiles = response.data.data.map(profile => ({
          ...profile,
          id: parseInt(profile.id),
          status: profile.status === "1" || profile.status === 1
        }));
        
        setProfiles(processedProfiles);
      } else {
        setError('Failed to load owner profiles');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      profile_image: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('profile_image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_owner_image.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.profile_image;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const getSocialIcon = (platform) => {
    switch(platform) {
      case 'facebook': return 'fab fa-facebook';
      case 'twitter': return 'fab fa-twitter';
      case 'instagram': return 'fab fa-instagram';
      case 'linkedin': return 'fab fa-linkedin';
      case 'youtube': return 'fab fa-youtube';
      default: return 'fas fa-link';
    }
  };

  const getSocialColor = (platform) => {
    switch(platform) {
      case 'facebook': return '#1877f2';
      case 'twitter': return '#1da1f2';
      case 'instagram': return '#e4405f';
      case 'linkedin': return '#0a66c2';
      case 'youtube': return '#ff0000';
      default: return '#6c757d';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.profile_image = uploadedUrl;
        } else {
          setError('Failed to upload profile image');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingProfile) {
        response = await axios.put(
          `${API_BASE_URL}/owner_profiles.php?id=${editingProfile.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/owner_profiles.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingProfile ? 'Profile updated!' : 'Profile added!');
        setShowModal(false);
        resetForm();
        setEditingProfile(null);
        fetchProfiles();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving profile: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this profile? This will also delete the associated profile image.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/owner_profiles.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Profile deleted!');
          fetchProfiles();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete profile: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting profile');
        console.error(err);
      }
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      full_name: profile.full_name,
      phone: profile.phone || '',
      email: profile.email || '',
      profile_image: profile.profile_image || '',
      bio: profile.bio || '',
      facebook: profile.facebook || '',
      twitter: profile.twitter || '',
      instagram: profile.instagram || '',
      linkedin: profile.linkedin || '',
      youtube: profile.youtube || '',
      experience: profile.experience || '',
      professional_roles: profile.professional_roles || '',
      qualifications: profile.qualifications || '',
      status: profile.status === true || profile.status === "1" || profile.status === 1
    });
    
    // Set image preview if profile_image exists
    if (profile.profile_image) {
      setImagePreview(profile.profile_image);
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (profile) => {
    try {
      const currentActive = profile.status === true || profile.status === "1" || profile.status === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        full_name: profile.full_name,
        phone: profile.phone || '',
        email: profile.email || '',
        profile_image: profile.profile_image || '',
        bio: profile.bio || '',
        facebook: profile.facebook || '',
        twitter: profile.twitter || '',
        instagram: profile.instagram || '',
        linkedin: profile.linkedin || '',
        youtube: profile.youtube || '',
        experience: profile.experience || '',
        professional_roles: profile.professional_roles || '',
        qualifications: profile.qualifications || '',
        status: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/owner_profiles.php?id=${profile.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchProfiles();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProfile(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No bio';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const socialPlatforms = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'];

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading owner profiles...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Owner Profiles Manager</h2>
            <p className="text-muted">Manage owner/founder profiles for your website</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Profile
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="80">Photo</th>
                  <th>Name & Info</th>
                  <th>Contact</th>
                  <th>Social Media</th>
                  <th>Professional Info</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr key={profile.id}>
                    <td>
                      {profile.profile_image ? (
                        <Image 
                          src={getImageUrl(profile.profile_image)} 
                          alt={profile.full_name}
                          roundedCircle
                          thumbnail 
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-user text-white fa-2x"></i>
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{profile.full_name}</strong>
                      {profile.professional_roles && (
                        <div className="text-muted small">{profile.professional_roles}</div>
                      )}
                      {profile.experience && (
                        <Badge bg="info" className="mt-1">{profile.experience}</Badge>
                      )}
                    </td>
                    <td>
                      <div className="small">
                        {profile.email && (
                          <div>
                            <i className="fas fa-envelope me-1"></i>
                            {profile.email}
                          </div>
                        )}
                        {profile.phone && (
                          <div>
                            <i className="fas fa-phone me-1"></i>
                            {profile.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {socialPlatforms.map(platform => (
                          profile[platform] && (
                            <a 
                              key={platform}
                              href={profile[platform]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                              style={{ color: getSocialColor(platform) }}
                              title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                            >
                              <i className={`${getSocialIcon(platform)} fa-lg me-1`}></i>
                            </a>
                          )
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="small text-muted">
                        {profile.qualifications && (
                          <div>
                            <i className="fas fa-graduation-cap me-1"></i>
                            {profile.qualifications}
                          </div>
                        )}
                        {profile.bio && (
                          <div className="mt-1">
                            {truncateText(profile.bio, 40)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={profile.status === true || profile.status === "1" || profile.status === 1}
                        onChange={() => handleToggleActive(profile)}
                        label={(profile.status === true || profile.status === "1" || profile.status === 1) ? 'Active' : 'Inactive'}
                        key={`active-${profile.id}-${profile.status}`}
                      />
                      {profile.status && (
                        <small className="text-success d-block">
                          <i className="fas fa-check-circle me-1"></i>
                          Currently displayed
                        </small>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(profile.created_at)}
                      </small>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleEdit(profile)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(profile.id)}
                        disabled={profile.status}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {profiles.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-user-tie fa-3x mb-3"></i>
                <p>No owner profiles found. Add your first profile!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Profile Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingProfile ? 'Edit Owner Profile' : 'Add Owner Profile'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={4}>
                  {/* Profile Photo Upload */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Profile Photo</h6>
                    </Card.Header>
                    <Card.Body className="text-center">
                      <div className="mb-3">
                        {(imagePreview || formData.profile_image) ? (
                          <Image 
                            src={imagePreview?.startsWith('data:') ? imagePreview : getImageUrl(formData.profile_image)}
                            alt="Profile Preview" 
                            roundedCircle
                            style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                            className="border shadow"
                          />
                        ) : (
                          <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto"
                            style={{ width: '200px', height: '200px' }}>
                            <i className="fas fa-user-tie text-white fa-5x"></i>
                          </div>
                        )}
                      </div>
                      
                      <Form.Group>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          disabled={uploadingImage}
                        />
                        <Form.Text className="text-muted">
                          Upload profile photo (square, recommended: 400x400px)
                        </Form.Text>
                      </Form.Group>
                      
                      {(imagePreview || formData.profile_image) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="mt-2"
                          onClick={removeImage}
                        >
                          <i className="fas fa-trash me-1"></i> Remove Photo
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                  
                  {/* Social Media Links */}
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Social Media Links</h6>
                    </Card.Header>
                    <Card.Body>
                      {socialPlatforms.map(platform => (
                        <InputGroup className="mb-3" key={platform}>
                          <InputGroup.Text>
                            <i className={getSocialIcon(platform)} style={{ color: getSocialColor(platform) }}></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="url"
                            name={platform}
                            value={formData[platform]}
                            onChange={handleInputChange}
                            placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`}
                          />
                        </InputGroup>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={8}>
                  {/* Personal Information */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Personal Information</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Full Name *</Form.Label>
                            <Form.Control
                              type="text"
                              name="full_name"
                              value={formData.full_name}
                              onChange={handleInputChange}
                              required
                              placeholder="John Doe"
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="+1 234 567 8900"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Bio</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="bio"
                          value={formData.bio}
                          onChange={handleTextareaChange}
                          rows={4}
                          placeholder="Write a brief biography about the owner..."
                        />
                        <Form.Text className="text-muted">
                          Tell the story and background of the owner
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                  
                  {/* Professional Information */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Professional Information</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Experience</Form.Label>
                            <Form.Control
                              type="text"
                              name="experience"
                              value={formData.experience}
                              onChange={handleInputChange}
                              placeholder="e.g., 10+ years in Web Development"
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Qualifications</Form.Label>
                            <Form.Control
                              type="text"
                              name="qualifications"
                              value={formData.qualifications}
                              onChange={handleInputChange}
                              placeholder="e.g., BSc Computer Science, MBA"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Professional Roles</Form.Label>
                        <Form.Control
                          type="text"
                          name="professional_roles"
                          value={formData.professional_roles}
                          onChange={handleInputChange}
                          placeholder="e.g., CEO, Founder, Lead Developer"
                        />
                        <Form.Text className="text-muted">
                          Separate multiple roles with commas
                        </Form.Text>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <div>
                          <Form.Check
                            type="switch"
                            name="status"
                            label="Active (Will be displayed on website)"
                            checked={formData.status}
                            onChange={handleInputChange}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Only one profile can be active at a time
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                  
                  {/* Preview Section */}
                  <Card className="border-primary">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Preview</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex align-items-start">
                        <div className="me-4">
                          {(imagePreview || formData.profile_image) ? (
                            <Image 
                              src={imagePreview?.startsWith('data:') ? imagePreview : getImageUrl(formData.profile_image)}
                              alt="Profile Preview" 
                              roundedCircle
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="border"
                            />
                          ) : (
                            <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
                              style={{ width: '100px', height: '100px' }}>
                              <i className="fas fa-user-tie text-white fa-3x"></i>
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="mb-1">{formData.full_name || 'Owner Name'}</h4>
                          {formData.professional_roles && (
                            <div className="text-primary mb-2">
                              {formData.professional_roles}
                            </div>
                          )}
                          {formData.experience && (
                            <Badge bg="info" className="me-2">{formData.experience}</Badge>
                          )}
                          {formData.qualifications && (
                            <Badge bg="secondary" className="me-2">{formData.qualifications}</Badge>
                          )}
                          <Badge bg={formData.status ? 'success' : 'warning'} className="me-2">
                            {formData.status ? 'Active' : 'Inactive'}
                          </Badge>
                          
                          {(formData.email || formData.phone) && (
                            <div className="mt-2">
                              {formData.email && (
                                <small className="text-muted me-3">
                                  <i className="fas fa-envelope me-1"></i>
                                  {formData.email}
                                </small>
                              )}
                              {formData.phone && (
                                <small className="text-muted">
                                  <i className="fas fa-phone me-1"></i>
                                  {formData.phone}
                                </small>
                              )}
                            </div>
                          )}
                          
                          <div className="mt-2">
                            {socialPlatforms.map(platform => (
                              formData[platform] && (
                                <a 
                                  key={platform}
                                  href="#"
                                  className="text-decoration-none me-3"
                                  style={{ color: getSocialColor(platform) }}
                                >
                                  <i className={`${getSocialIcon(platform)} fa-lg`}></i>
                                </a>
                              )
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {formData.bio && (
                        <div className="mt-3">
                          <h6>Bio Preview:</h6>
                          <p className="text-muted">
                            {truncateText(formData.bio, 200)}
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              {editingProfile && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created: {formatDate(editingProfile.created_at)}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingProfile.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingProfile ? 'Update' : 'Add'} Profile
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default OwnerProfilesManager;